/**
 * The Nextcloud folder in the Files module (#98 part 3,
 * docs/nextcloud-folder-in-files-design.md): routes, links, the root row and
 * where an error sends the user; part 4 adds file references and the texts of
 * Analyze / Open errors. Pure helpers; the module wires them in.
 */

import {
  NEXTCLOUD_MOUNT_ID,
  type FolderItem,
} from '../../../core/files-service';
import { isNotFound } from '../../../core/auth-utils';
import {
  NEXTCLOUD_SUBROUTE,
  parseNextcloudSubroute,
} from '../../../core/nextcloud-route-token';
import { isIntegrationError } from '../../../core/integrations-service';
import {
  GatewayErrorCode,
  isGatewayError,
} from '../../../core/gateway/error-codes';

/** The proxy's id pattern (gitterdan-website FILES_ID_PATTERN). */
export const OWN_FOLDER_SUBROUTE =
  /^folder\/([A-Za-z0-9][A-Za-z0-9._:-]{0,199})$/;

export type FilesRoute =
  | { kind: 'own'; folderId: string | null }
  | { kind: 'nextcloud'; exposedId: string | null; path: string };

export function acceptsFilesSubroute(
  subroute: string,
  nextcloud: boolean
): boolean {
  // Both patterns are anchored with bounded repeats over classes that exclude
  // the separators, so matching is linear (no catastrophic backtracking).
  return (
    OWN_FOLDER_SUBROUTE.test(subroute) || // nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
    (nextcloud && NEXTCLOUD_SUBROUTE.test(subroute)) // nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
  );
}

/**
 * The route for a subroute; null for a Nextcloud route whose token does not
 * decode (the caller sends the user to `fallback`).
 */
export function parseFilesSubroute(
  subroute: string | null
): { route: FilesRoute } | { fallback: string } {
  if (!subroute) return { route: { kind: 'own', folderId: null } };
  const own = OWN_FOLDER_SUBROUTE.exec(subroute);
  if (own) return { route: { kind: 'own', folderId: own[1]! } };
  const nc = parseNextcloudSubroute(subroute);
  if (nc) return { route: { kind: 'nextcloud', ...nc } };
  const exposedId = /^nextcloud\/([a-f0-9]{32})\//.exec(subroute)?.[1];
  return {
    fallback: exposedId ? `/files/nextcloud/${exposedId}` : '/files/nextcloud',
  };
}

export function sameRoute(a: FilesRoute, b: FilesRoute): boolean {
  return a.kind === 'own'
    ? b.kind === 'own' && a.folderId === b.folderId
    : b.kind === 'nextcloud' &&
        a.exposedId === b.exposedId &&
        a.path === b.path;
}

/** The hash path for a FileList item or crumb id in the current mode. */
export function folderPathFor(route: FilesRoute, id: string | null): string {
  if (id === null) return '/files';
  if (route.kind === 'own') {
    return id === NEXTCLOUD_MOUNT_ID
      ? '/files/nextcloud'
      : `/files/folder/${id}`;
  }
  return id === '' ? '/files/nextcloud' : `/files/nextcloud/${id}`;
}

/** The row at the Files root that opens the Nextcloud folder. */
export function mountRow(name: string): FolderItem {
  return {
    kind: 'folder',
    id: NEXTCLOUD_MOUNT_ID,
    name,
    createdAt: '',
    createdBy: null,
    canRename: false,
    canDelete: false,
  };
}

/**
 * Where a failed Nextcloud listing sends the user (text key + path), or null
 * when it stays on the page with the error state.
 */
export function nextcloudErrorRoute(
  error: unknown,
  route: { exposedId: string | null; path: string }
): { messageKey: string; navigate: string } | null {
  if (isIntegrationError(error)) {
    if (error.code === 'INVALID_STATE' || error.code === 'NEEDS_RECONNECT') {
      return {
        messageKey: 'modules.files.nextcloud.unavailableNow',
        navigate: '/files',
      };
    }
    if (error.code === 'FOLDER_UNAVAILABLE') {
      return {
        messageKey: 'modules.files.nextcloud.folderUnavailable',
        navigate: '/files/nextcloud',
      };
    }
    return null;
  }
  if (isNotFound(error) && route.exposedId === null) {
    // The mount itself: the integration is switched off on the server.
    return {
      messageKey: 'modules.files.nextcloud.unavailableNow',
      navigate: '/files',
    };
  }
  if (isNotFound(error) && route.exposedId !== null) {
    return {
      messageKey: 'modules.files.nextcloud.folderGone',
      navigate:
        route.path === ''
          ? '/files/nextcloud'
          : `/files/nextcloud/${route.exposedId}`,
    };
  }
  return null;
}

/** Text for a failed New folder in Nextcloud, shown inside the Name dialog. */
export function nextcloudMkdirErrorKey(error: unknown): string | null {
  if (isIntegrationError(error)) {
    const keys: Record<string, string> = {
      DUPLICATE_NAME: 'modules.files.folderExists',
      INVALID_INPUT: 'modules.files.invalidName',
      QUOTA_EXCEEDED: 'modules.files.nextcloud.quotaExceeded',
      FOLDER_UNAVAILABLE: 'modules.files.nextcloud.folderUnavailable',
      NEEDS_RECONNECT: 'modules.files.nextcloud.unavailableNow',
      INVALID_STATE: 'modules.files.nextcloud.unavailableNow',
    };
    return keys[error.code] ?? null;
  }
  if (isGatewayError(error) && error.code === GatewayErrorCode.FORBIDDEN) {
    return 'modules.files.nextcloud.forbidden';
  }
  if (isNotFound(error)) return 'modules.files.nextcloud.folderGone';
  return null;
}

/** Exposed folder + path of a Nextcloud file row (#98 part 4); its id is a route id. */
export function nextcloudFileRef(
  id: string
): { folderId: string; path: string } | null {
  const parsed = parseNextcloudSubroute(`nextcloud/${id}`);
  return parsed?.exposedId && parsed.path
    ? { folderId: parsed.exposedId, path: parsed.path }
    : null;
}

/** Proxy codes of Analyze / Open with their own text (`modules.files.nextcloud.analysisErrors.<code>`). */
const ANALYSIS_ERROR_CODES: readonly string[] = [
  'ANALYSIS_LIMIT',
  'RATE_LIMITED',
  'TOO_LARGE',
  'UNSUPPORTED_TYPE',
  'ANALYSIS_UNAVAILABLE',
  'NOT_ANALYZED',
];

/**
 * A failed Analyze / Open (#98 part 4): the text key, and where to go (or that
 * the folder is to be reloaded); null when the generic Nextcloud text applies.
 */
export function nextcloudAnalysisError(
  error: unknown
): { messageKey: string; navigate?: string; reload?: boolean } | null {
  if (isIntegrationError(error)) {
    if (ANALYSIS_ERROR_CODES.includes(error.code)) {
      return {
        messageKey: `modules.files.nextcloud.analysisErrors.${error.code}`,
      };
    }
    if (error.code === 'FOLDER_UNAVAILABLE') {
      return {
        messageKey: 'modules.files.nextcloud.folderUnavailable',
        navigate: '/files/nextcloud',
      };
    }
    if (error.code === 'INVALID_STATE' || error.code === 'NEEDS_RECONNECT') {
      return {
        messageKey: 'modules.files.nextcloud.unavailableNow',
        navigate: '/files',
      };
    }
    return null;
  }
  if (isNotFound(error)) {
    return { messageKey: 'modules.files.nextcloud.fileGone', reload: true };
  }
  return null;
}
